import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Check, X, Lock } from 'lucide-react';
import { checkMaplePTConnection } from '@/lib/maplept';
import { useAppStore } from '@/store';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const MaplePTSettings: React.FC = () => {
  const { user } = useAppStore();
  const [isValidating, setIsValidating] = useState(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);

  const isPaid = user ? (user.subscription !== 'free') : false;

  const handleRefresh = async () => {
    setIsValidating(true);
    try {
      const ok = await checkMaplePTConnection();
      setIsConnected(ok);
    } catch {
      setIsConnected(false);
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    handleRefresh();
  }, []);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>MaplePT Settings</CardTitle>
        <CardDescription>
          MaplePT (OpenAI-compatible) is configured server-side and available for paid plans.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!isPaid && (
          <Alert>
            <Lock className="h-4 w-4" />
            <AlertTitle>Paid feature</AlertTitle>
            <AlertDescription>
              MaplePT is available on Basic, Standard, and Enterprise plans.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <p className="text-sm text-muted-foreground">Server Connection Status:</p>
            {isValidating ? (
              <div className="flex items-center text-sm">
                <Loader2 className="animate-spin h-4 w-4 mr-1 text-muted-foreground" />
                <span>Checking...</span>
              </div>
            ) : isConnected === null ? (
              <span className="text-sm font-medium">Unknown</span>
            ) : isConnected ? (
              <div className="flex items-center text-sm text-green-600">
                <Check className="h-4 w-4 mr-1" />
                <span>Connected</span>
              </div>
            ) : (
              <div className="flex items-center text-sm text-red-600">
                <X className="h-4 w-4 mr-1" />
                <span>Not connected</span>
              </div>
            )}
          </div>

          <Button onClick={handleRefresh} disabled={isValidating} variant="outline" className="w-full sm:w-auto">
            {isValidating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking Connection
              </>
            ) : (
              'Re-check Connection'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MaplePTSettings;